import React, { useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from "firebase/auth";
import { CreditCard, Eye, EyeOff, QrCode, X } from "lucide-react";

import { useLanguage } from "../contexts/LanguageContext";
import { auth } from "../firebase/client";
import {
  getSubscriptionCopy,
  getSubscriptionMonthlyEquivalent,
  getSubscriptionPeriodLabel,
  getSubscriptionPrice,
  type SubscriptionBilling,
  type SubscriptionPlan,
} from "../utils/subscriptionPricing";

export type PricingPlan = SubscriptionPlan;
export type PricingBilling = SubscriptionBilling;
type PayMethod = "credit_card" | "pix";
type Step = "auth" | "payment";
type AuthMode = "register" | "login";

const CHECKOUT_URL = "https://us-central1-orthoprecision-web.cloudfunctions.net";

const ERROR_COPY = {
  pt: {
    emailAlreadyInUse: "E-mail já cadastrado. Tente entrar.",
    wrongPassword: "Senha incorreta ou conta não encontrada.",
    weakPassword: "Senha muito fraca. Use pelo menos 6 caracteres.",
    userNotFound: "Nenhuma conta encontrada com este e-mail.",
    tooManyRequests: "Muitas tentativas. Aguarde um momento.",
    generic: "Erro ao autenticar. Tente novamente.",
  },
  en: {
    emailAlreadyInUse: "Email already in use. Try signing in.",
    wrongPassword: "Wrong password or account not found.",
    weakPassword: "Password is too weak. Use at least 6 characters.",
    userNotFound: "No account found for this email.",
    tooManyRequests: "Too many attempts. Please wait a moment.",
    generic: "Authentication failed. Please try again.",
  },
  es: {
    emailAlreadyInUse: "Correo ya registrado. Intenta entrar.",
    wrongPassword: "Contraseña incorrecta o cuenta no encontrada.",
    weakPassword: "La contraseña es muy débil. Usa al menos 6 caracteres.",
    userNotFound: "No se encontró una cuenta con este correo.",
    tooManyRequests: "Demasiados intentos. Espera un momento.",
    generic: "Error al autenticar. Intenta nuevamente.",
  },
} as const;

function mapFirebaseError(code: string, language: "pt" | "en" | "es"): string {
  const copy = ERROR_COPY[language];
  if (code.includes("email-already-in-use")) return copy.emailAlreadyInUse;
  if (code.includes("wrong-password") || code.includes("invalid-credential")) return copy.wrongPassword;
  if (code.includes("weak-password")) return copy.weakPassword;
  if (code.includes("user-not-found")) return copy.userNotFound;
  if (code.includes("too-many-requests")) return copy.tooManyRequests;
  if (code.includes("popup-closed")) return "";
  return copy.generic;
}

interface Props {
  plan: PricingPlan;
  billing: PricingBilling;
  onClose: () => void;
}

export default function PricingCheckoutModal({ plan, billing, onClose }: Props) {
  const { language } = useLanguage();
  const copy = getSubscriptionCopy(language);
  const [step, setStep] = useState<Step>("auth");
  const [authMode, setAuthMode] = useState<AuthMode>("register");
  const [authedUser, setAuthedUser] = useState<User | null>(null);

  const [payMethod, setPayMethod] = useState<PayMethod>("credit_card");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isPro = plan === "pro";
  const canCheckout = payMethod !== "pix";
  const planLabel = plan === "premium" ? "OrthoPrecision Premium" : "OrthoPrecision Pro";

  useEffect(() => {
    const current = auth.currentUser;
    if (current) {
      setAuthedUser(current);
      setStep("payment");
    }
  }, []);

  const toPayment = (user: User) => {
    setAuthedUser(user);
    setError(null);
    setStep("payment");
  };

  const handleGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      toPayment(result.user);
    } catch (err: unknown) {
      const msg = mapFirebaseError(err instanceof Error ? err.message : "", language);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const fn = authMode === "register" ? createUserWithEmailAndPassword : signInWithEmailAndPassword;
      const result = await fn(auth, email, password);

      if (authMode === "register") {
        try {
          await sendEmailVerification(result.user);
        } catch (verificationErr) {
          console.warn(
            "[PricingCheckoutModal] failed to send verification email:",
            verificationErr instanceof Error ? verificationErr.message : verificationErr
          );
        }
      }

      toPayment(result.user);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      const msg = mapFirebaseError(raw, language);
      if (msg) {
        setError(msg);
        if (raw.includes("email-already-in-use")) setAuthMode("login");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    if (!authedUser || !canCheckout) return;
    setError(null);
    setBusy(true);
    try {
      const token = await authedUser.getIdToken();
      const successUrl = `${window.location.origin}/app?checkout=success`;
      const cancelUrl = `${window.location.origin}/`;
      const interval = billing === "annual" ? "yearly" : "monthly";

      const resp = await fetch(`${CHECKOUT_URL}/createCheckoutSessionHttp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: plan, interval, successUrl, cancelUrl }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error ?? "Falha ao criar checkout");
      window.location.href = data.url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erro ao iniciar pagamento.");
      setBusy(false);
    }
  };

  const ctaColor = isPro
    ? "bg-cyan-500 text-slate-950 shadow-cyan-500/20 hover:bg-cyan-400"
    : "bg-violet-600 text-white shadow-violet-500/20 hover:bg-violet-500";

  const selectedPrice = useMemo(() => getSubscriptionPrice(language, plan, billing), [billing, language, plan]);
  const selectedPeriod = useMemo(() => getSubscriptionPeriodLabel(language, billing), [billing, language]);
  const annualEquivalent = useMemo(() => getSubscriptionMonthlyEquivalent(language, plan), [language, plan]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/60">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
              {step === "auth" ? copy.authStepTitle : copy.confirmStepTitle}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h3 className="text-base font-bold text-white">{planLabel}</h3>
              <span
                className={[
                  "rounded-full px-2 py-0.5 text-xs font-bold",
                  isPro ? "bg-cyan-500/15 text-cyan-400" : "bg-violet-500/15 text-violet-400",
                ].join(" ")}
              >
                {selectedPrice}
                {selectedPeriod}
              </span>
            </div>
            {billing === "annual" && <p className="mt-0.5 text-xs text-slate-500">{annualEquivalent}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-900 hover:text-white"
            aria-label={copy.close}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {step === "auth" ? (
            <div className="space-y-4">
              <button
                onClick={handleGoogle}
                disabled={busy}
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-700 bg-slate-900 py-3 text-sm font-semibold text-white transition-all hover:border-slate-600 hover:bg-slate-800 disabled:opacity-60"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {copy.googleSignIn}
              </button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-800" />
                <span className="text-xs text-slate-600">ou</span>
                <div className="h-px flex-1 bg-slate-800" />
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-3">
                <input
                  type="email"
                  placeholder={copy.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition focus:border-slate-600"
                />
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    placeholder={copy.passwordPlaceholder}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 pr-10 text-sm text-white placeholder-slate-600 outline-none transition focus:border-slate-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400"
                    aria-label={showPwd ? copy.close : copy.passwordPlaceholder}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}

                <button
                  type="submit"
                  disabled={busy}
                  className={`w-full rounded-xl py-3 text-sm font-bold shadow-lg transition-all disabled:opacity-60 ${ctaColor}`}
                >
                  {busy
                    ? copy.openingCheckout
                    : authMode === "register"
                      ? copy.createAccountAndContinue
                      : copy.signInAndContinue}
                </button>
              </form>

              <p className="text-center text-xs text-slate-500">
                {authMode === "register" ? (
                  <>
                    {copy.alreadyHaveAccount}{" "}
                    <button
                      onClick={() => {
                        setAuthMode("login");
                        setError(null);
                      }}
                      className="text-slate-300 hover:text-white"
                    >
                      {copy.signIn}
                    </button>
                  </>
                ) : (
                  <>
                    {copy.noAccount}{" "}
                    <button
                      onClick={() => {
                        setAuthMode("register");
                        setError(null);
                      }}
                      className="text-slate-300 hover:text-white"
                    >
                      {copy.createAccount}
                    </button>
                  </>
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white">
                  {authedUser?.email?.[0]?.toUpperCase() ?? "U"}
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">{copy.accountLabel}</p>
                  <p className="truncate text-sm text-white">{authedUser?.email}</p>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                  {copy.paymentMethod}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPayMethod("credit_card")}
                    className={[
                      "flex flex-1 items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all",
                      payMethod === "credit_card"
                        ? "border-slate-600 bg-slate-800 text-white"
                        : "border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200",
                    ].join(" ")}
                  >
                    <CreditCard className="h-4 w-4" />
                    {copy.creditCard}
                  </button>
                  <button
                    onClick={() => setPayMethod("pix")}
                    className={[
                      "flex flex-1 items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all",
                      payMethod === "pix"
                        ? "border-slate-600 bg-slate-800 text-white"
                        : "border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200",
                    ].join(" ")}
                  >
                    <QrCode className="h-4 w-4" />
                    {copy.pix}
                  </button>
                </div>
              </div>

              {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}

              <button
                onClick={handleCheckout}
                disabled={busy || !canCheckout}
                className={`w-full rounded-xl py-3.5 text-sm font-bold shadow-lg transition-all disabled:opacity-60 ${ctaColor}`}
              >
                {busy
                  ? copy.openingCheckout
                  : !canCheckout
                    ? copy.pixComingSoon
                    : copy.subscribePlan(planLabel)}
              </button>

              <button
                onClick={() => {
                  setStep("auth");
                  setError(null);
                }}
                className="w-full py-2 text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
              >
                {copy.back}
              </button>

              <p className="text-center text-[11px] text-slate-600">
                {payMethod === "pix" ? copy.pix30Days : copy.cardRecurring}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
